import { z } from 'zod';
import { Nullable } from 'global';
import { dealBuilderFormSchema } from './model/schema';

export enum DealBuilderStep {
  Contacts = 'contacts',
  Equipment = 'equipment',
  Pricing = 'pricing',
  FinanceMenu = 'financeMenu',
  ReviewSend = 'reviewSend',
}

export interface StepConfig {
  step: DealBuilderStep;
  label: string;
  subLabel?: string;
}

export interface DealBuilderSummary {
  hardCost: number;
  feesAndTaxes: number;
  customerDeposit: number;
  tradeIn: number;
  balanceDue: number;
}

export type DealBuilderSummaryItemColor = 'default' | 'secondary' | 'warning' | 'info';

export interface DealBuilderSummaryItemConfig {
  key: keyof DealBuilderSummary;
  label: string;
  color: DealBuilderSummaryItemColor;
}

export type DealBuilderFormValues = z.infer<typeof dealBuilderFormSchema>;

// Raw shapes consumed at the module boundary (VO/CreditApplication API responses)
export interface DealBuilderPotentialCustomerSource {
  id?: Nullable<string>;
  name?: Nullable<string>;
  address?: Nullable<string>;
}

export interface DealBuilderVOSource {
  vendorOpportunityId?: Nullable<string>;
  dynamicsVendorOpportunityId?: Nullable<string>;
  potentialCustomer?: Nullable<DealBuilderPotentialCustomerSource>;
}

export interface DealBuilderCreditApplicationSource {
  id?: Nullable<string>;
}

export interface DealBuilderEquipmentSource {
  assets: { id: string; source: string }[];
  equipmentId: string;
  year: Nullable<string>;
  make: Nullable<string>;
  model: Nullable<string>;
  serialNumberVin: Nullable<string>;
  stockId: Nullable<string>;
  newUsed: Nullable<string>;
  wholesaleCost: Nullable<number>;
  mileage: Nullable<number>;
  additionalDescription: Nullable<string>;
  inventoryItem: Nullable<{ inventoryId: Nullable<string> }>;
}

// Normalized view-model used by the Contacts step
export interface DealBuilderAccount {
  name: Nullable<string>;
  address: Nullable<string>;
}

export interface DealBuilderContact {
  ownerPgId: Nullable<string>;
  accountContactId: Nullable<string>;
  fullName: string;
  title: Nullable<string>;
  email: Nullable<string>;
  phone: Nullable<string>;
}

export interface DealBuilderContactsData {
  account: Nullable<DealBuilderAccount>;
  primaryContact: Nullable<DealBuilderContact>;
  additionalRecipients: DealBuilderContact[];
  allContacts: DealBuilderContact[];
}

export interface SelectedContactsSummary {
  mainContact: Nullable<DealBuilderContact>;
  additionalCount: number;
  avatarIndex: number;
}
